/**
 * The shelf-detail screen (P2.8, UI_PLAN §3 level 3) — mounted at
 * `#/map/<shelfId>`. Reachable today from the Capture tab's *"open the
 * shelf →"* chip and by direct URL; there is no Map tab yet to drill down
 * from (pillar 6), so this is the WHOLE of "level 3" standing alone until
 * levels 1-2 exist to lead into it (see `lib/route.ts`'s own note).
 *
 * Photo · last-read date and declared depth · a soft staleness line · the
 * ALWAYS-visible depth bar (§5.7 — even at `depth_count` 1, for discovery)
 * · the books at the selected depth (the durable §5.6 list, never
 * auto-pruned) · read history as diffs. This IS the history UI; there is no
 * run list anywhere else (§5.5).
 */
import { Address, useShelfWhere } from '../lib/Address'
import { useI18n } from '../lib/i18n'
import { formatDate, type Lang } from '@booksnap/ui'
import { CopyBadges, StatusBadge } from '../books/Feed'
import { ReadHistory } from './ReadHistory'
import { useShelfDetail } from './useShelfDetail'
import { AttachPhoto } from './AttachPhoto'
import { attachShelfPhoto, imageUrl, type Book, type DepthStatusDTO } from '../api/client'

export interface ShelfPageProps {
  shelfId: string
  onBack: () => void
  onOpen: (bookId: string) => void
}

function stalenessLine(
  depths: DepthStatusDTO[],
  t: ReturnType<typeof useI18n>['t'],
  // `Lang`, not `string`: the shared formatter takes the union, so a locale
  // this app has no strings for cannot reach the date formatting either.
  lang: Lang,
): string | null {
  const stale = depths.filter((d) => d.is_stale)
  if (stale.length === 0) return null
  return stale
    .map((d) => {
      const row = t.depth_n(d.depth)
      return d.last_read_at
        ? t.stale_since(row, formatDate(d.last_read_at, lang))
        : t.stale_never(row)
    })
    .join(' · ')
}

function BookRow({ book, onOpen }: { book: Book; onOpen: (id: string) => void }) {
  const { t } = useI18n()
  const streak = book.copies[0]?.not_seen_streak ?? null
  return (
    <button type="button" className="brow" onClick={() => onOpen(book.id)}>
      <span className="meta">
        <span className="t rtl-safe">{book.title}</span>
        <span className="a rtl-safe">{book.author || `— ${t.author_label} —`}</span>
      </span>
      <span className="chiprow">
        <StatusBadge status={book.status} />
        <CopyBadges book={book} />
        {streak !== null && streak > 0 && (
          <span className="badge b-review" title={
            streak === 1 ? t.not_seen_streak_one : t.not_seen_streak_n(streak)
          }>
            {streak === 1 ? t.not_seen_streak_one : t.not_seen_streak_n(streak)}
          </span>
        )}
      </span>
    </button>
  )
}

export function ShelfPage({ shelfId, onBack, onOpen }: ShelfPageProps) {
  const { t, lang } = useI18n()
  const shelfDetail = useShelfDetail(shelfId)
  // ⚠ Asked for the SHELF, with no depth: the depth bar below is how this
  // screen chooses a row, and printing the selected one up here would make
  // the heading change under a person who was only browsing rows. A copy's
  // row is named on the BOOK surface, which is where one copy is the subject.
  const where = useShelfWhere(shelfId)
  const { state } = shelfDetail
  const stale = state.kind === 'ready' ? stalenessLine(state.depths, t, lang) : null

  return (
    <div className="bookpage shelfpage">
      <button type="button" className="btn ghost" onClick={onBack}>
        ← {t.back}
      </button>

      {state.kind === 'loading' && <p className="loading">{t.loading}</p>}
      {state.kind === 'missing' && <p className="empty">{t.shelf_not_found}</p>}
      {/* ⚠ A refusal and a failure are two sentences, and one of them has a
          BUTTON. "Absent is not unknown" is the rule the sibling item cites;
          here the cost of collapsing them is that a dropped connection reads
          as *your shelf is gone*. */}
      {state.kind === 'failed' && (
        <div className="errorbox" role="alert">
          <p>{t.shelf_unreachable}</p>
          <p className="tiny muted">{state.why}</p>
          <button type="button" className="btn ghost"
                  onClick={() => void shelfDetail.reload()}>
            {t.retry}
          </button>
        </div>
      )}

      {state.kind === 'ready' && (
        <>
          <div className="panel shelfhero">
            <div className="body">
              <div className="shelfheroTop">
                {shelfDetail.photoImageId ? (
                  <img
                    className="shelfphoto"
                    src={imageUrl(shelfDetail.photoImageId)}
                    alt={t.shelf_untitled_photo_alt}
                  />
                ) : (
                  <div className="shelfphoto shelfphoto-empty" aria-hidden="true" />
                )}
                <div className="shelfheroMeta">
                  <h2 className="rtl-safe">{state.shelf.label || t.unassigned}</h2>
                  {/* Where it stands — the half of "level 3" that had no
                      answer until the map could give one (P6.5b). Silent
                      while it loads and silent if the lookup fails: an
                      address is not this screen's subject, and a grey
                      apology where a line of furniture should be is worse
                      than nothing. A shelf that stands NOWHERE does get a
                      sentence, because that is a fact about the shelf and
                      the thing a person would go and fix. */}
                  {where.data && (
                    <div className="shelfwhere">
                      <Address where={where.data} note={false} onMap />
                    </div>
                  )}
                  <p className="muted">
                    {state.lastReadAt
                      ? t.shelf_last_read(formatDate(state.lastReadAt, lang))
                      : t.shelf_never_read}
                  </p>
                  {stale && <p className="tiny stalenote">{stale}</p>}
                  {/* ⚠ §3.11's last promise, and the ADDRESS half is the one
                      worth having: *"the shelf that was at section 1, column
                      2, level 3"* is what a person reads off a drawing, and
                      it is the half that survives in someone's memory when
                      the id does not. A merge would otherwise destroy the
                      household's name for the absorbed shelf — a shelf is
                      "the one with the cookbooks" long after the drawing has
                      been rearranged. */}
                  {/* ⚠ Label, else ADDRESS, else NOTHING — and the third
                      branch is the one a review measured. On the owner's
                      library **0 of 154 shelves carry a label**, so
                      `label || t.unassigned` rendered *"היה גם: לא משויך"*
                      three times over for three merges: N identical panels
                      for one cause, saying nothing anybody can act on. An
                      unnamed shelf that also stood nowhere has nothing to
                      say, and silence is better than a line that says so.
                      ⚠ Newest first. `list_aliases` orders by `alias_id`,
                      which is uuid4 — random to a person reading *what this
                      shelf was*. */}
                  {[...(state.shelf.formerly ?? [])]
                    .sort((a, b) => (b.merged_at || '').localeCompare(
                      a.merged_at || ''))
                    .map((was) => {
                      const said = was.label
                        || (was.address
                          ? t.shelf_formerly_at(was.address.col,
                                                was.address.level)
                          : '')
                      return said ? (
                        <p className="tiny muted rtl-safe" key={was.id}>
                          {t.shelf_formerly(said)}
                        </p>
                      ) : null
                    })}
                </div>
              </div>

              {/* Always visible, even at depth_count 1 (§5.7) — most owners
                  never learn depth exists unless it's offered before they
                  need it, same reasoning as the Capture tab's own row. */}
              <div className="depthbar" role="group" aria-label={t.depth_bar_label}>
                {state.depths.map((d) => (
                  <button
                    key={d.depth}
                    type="button"
                    className={`depthbtn${shelfDetail.depth === d.depth ? ' on' : ''}`}
                    aria-pressed={shelfDetail.depth === d.depth}
                    onClick={() => shelfDetail.setDepth(d.depth)}
                  >
                    {t.depth_n(d.depth)}
                    {d.is_stale && <span className="dot" aria-hidden="true" />}
                  </button>
                ))}
                <button
                  type="button"
                  className="linkish"
                  disabled={shelfDetail.addingRow}
                  onClick={() => void shelfDetail.addRowBehind()}
                >
                  {t.add_row_behind}
                </button>
              </div>

              {/*
                P6.7d. Beneath the depth bar on purpose: the photo is OF a
                row, so the control that files one belongs under the control
                that chooses which row you are looking at — and it names the
                depth in its own label when the shelf has more than one.

                ⚠ Reload after, not an optimistic paint. `photoImageId` is
                the FIRST capture at this depth (order 0), so filing a second
                changes nothing on screen, and a control that claimed
                otherwise would be lying about which picture is the shelf's.
              */}
              <AttachPhoto
                depth={shelfDetail.depth}
                depthCount={state.shelf.depth_count}
                onAttach={async (photo) => {
                  await attachShelfPhoto(shelfId, shelfDetail.depth, photo)
                  shelfDetail.reload()
                }}
              />
            </div>
          </div>

          <div className="panel shelfbooks">
            <h3>
              <span>{t.shelf_books_title}</span>
              <span className="muted tiny">{shelfDetail.books.length}</span>
            </h3>
            <div className="body">
              {shelfDetail.booksLoading && <p className="loading">{t.loading}</p>}
              {shelfDetail.booksError && (
                <p className="errorbox" role="alert">{shelfDetail.booksError}</p>
              )}
              {!shelfDetail.booksLoading && shelfDetail.books.length === 0 && (
                <p className="empty">{t.shelf_books_empty}</p>
              )}
              {!shelfDetail.booksLoading && shelfDetail.books.length > 0 && (
                <div className="booklist">
                  {shelfDetail.books.map((b) => (
                    <BookRow key={`${b.id}:${b.copies[0]?.id ?? ''}`} book={b}
                            onOpen={onOpen} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <ReadHistory reads={shelfDetail.history} loading={shelfDetail.historyLoading} />
        </>
      )}
    </div>
  )
}
